import { useMemo } from "react"
import { SisetuResult } from "interface/sisetu"

import {
  Stack, StackProps, Box, 
  Typography, IconButton,
  Chip, 
  Tooltip
} from "@mui/material"

import {
  HeisaIcon, KyuuinIcon,
  CloseIcon,
  SearchIcon,
  PreviousIcon,
  NextIcon
} from 'shared/icons'

import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'

import { HospitalFlagChip } from "./basicPage/components/hospitalFlagChip"
import { useTenant } from "features/tenant/context"
import { useSearch } from "features/search/context"
import { useSisetuPreview } from "../container"

const googleSearchUrl = 'https://www.google.com/search?q='

const ellipsisStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}

type SisetuPreviewHeaderProps = {
  sisetu: SisetuResult
  container: ReturnType<typeof useSisetuPreview>
} & StackProps
export const SisetuPreviewHeader = ({
  sisetu,
  container: {
    onClose,
    hasNext, hasPrev,
    openNext, openPrev
  },
  ...others
}: SisetuPreviewHeaderProps) => {
	const tenant = useTenant()
  const search = useSearch()
  const searchWord = useMemo<string | null>(
    () => sisetu.sisetuName ? `"${sisetu.sisetuName}"` : null,
    [sisetu]
  )
  return (
    <Stack direction='column' padding={1} overflow="hidden" {...others}>
      <Stack direction="row" spacing={0.5} alignItems={'stretch'}>
        <Stack flexGrow={1} direction="row" spacing={1} alignItems="center" overflow="hidden">
          { sisetu.heisa ? <Chip icon={<HeisaIcon/>} label="閉鎖" color="error"/> : <></>}
          { sisetu.kyuuin ? <Chip icon={<KyuuinIcon/>} label="休院" /> : <></>}
          <Box flexGrow={1} padding={0.5} overflow="hidden">
            <Typography component="div" variant='caption' lineHeight={1} {...ellipsisStyle}>
              {sisetu.sisetuNameKana} / {sisetu.ryakusikiSisetuName}
            </Typography>
            <Typography component="div" variant='h5' gutterBottom sx={{ margin: 0 }} {...ellipsisStyle}>
              {sisetu.sisetuName}
            </Typography>
          </Box>
        </Stack>

        <Stack direction='row' alignItems="flex-start">
          {searchWord ? (
            <Tooltip title="施設名で検索">
              <IconButton
                href={`${googleSearchUrl}${searchWord}`}
                target="_blank"
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton disabled>
              <SearchIcon />
            </IconButton>
          )}

          {(hasPrev || hasNext) ? (
            <Stack direction="row">
              {hasPrev ? (
                <Tooltip title="前へ">
                  <IconButton onClick={openPrev}><PreviousIcon /></IconButton>
                </Tooltip>
              ) : (
                <IconButton disabled><PreviousIcon /></IconButton>
              )}
              {hasNext ? (
                <Tooltip title="次へ">
                  <IconButton onClick={openNext}><NextIcon /></IconButton>
                </Tooltip>
              ) : (
                <IconButton disabled><NextIcon /></IconButton>
              )}
            </Stack>
          ) : <></>}

          {onClose && (
            <>
              <Tooltip title="別のウィンドウで開く">
                <IconButton
                  href={search ? (
                    `/${tenant.domain}/search/${search.searchId}/sisetu/${sisetu.sisetuCode}`
                  ) : (
                    `/${tenant.domain}/sisetu/${sisetu.sisetuCode}`
                  )}
                  target='_blank'
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title='閉じる'>
                <IconButton onClick={onClose}>
                  <CloseIcon/>
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        { sisetu.keieitai      && <HospitalFlagChip variant="filled" size="small" value={1} name={sisetu.keieitai.shortName} tips={sisetu.keieitai.name}/> }
        { sisetu.byouinSyubetu && <HospitalFlagChip variant="filled" size="small" value={1} name={sisetu.byouinSyubetu.shortName} tips={sisetu.byouinSyubetu.name} /> }
        { sisetu.sisetuKubun   && <HospitalFlagChip variant="filled" size="small" value={1} name={sisetu.sisetuKubun.shortName} tips={sisetu.sisetuKubun.name} /> }
        { sisetu.hospitalFlags?.map((flag, i) => <Chip key={`flag-${flag.functionId}-${i}`} variant="outlined" size="small" label={flag.functionName} />) }
        { sisetu.kensa ? (
          <>
            { sisetu.kensa.biseibutu && <Chip variant="outlined" size="small" label='微生物検査' /> }
            { sisetu.kensa.kessei    && <Chip variant="outlined" size="small" label='血清検査' /> }
            { sisetu.kensa.ketueki   && <Chip variant="outlined" size="small" label='血液検査' /> }
            { sisetu.kensa.byouri    && <Chip variant="outlined" size="small" label='病理検査' /> }
            { sisetu.kensa.kiseichuu && <Chip variant="outlined" size="small" label='寄生虫検査' /> }
            { sisetu.kensa.seika     && <Chip variant="outlined" size="small" label='生化学検査' /> }
            { sisetu.kensa.ri        && <Chip variant="outlined" size="small" label='RI検査' /> }
          </>
        ) : <></> }
      </Stack>
    </Stack>
  )
}
